import type {RevaLanguage} from "../i18n";
import {type FormatParameterCategory, type FormatParameterDef, formatParameterValue} from "../number-format";
import {ALL_SOURCES, hasSpfTable, normalizeSpf, SPF_PARAMS} from "../parameter-resolution";
import {SECTION_II_ENTRY_PARAMS, SECTION_II_VALUE_FIELDS, sectionIIRowsFromEntryParams} from "../register";
import type {RevaSpecOutputParameter} from "../spec-conversion";

export type CommentForAttachment = {
    parameter: string;
    /** Parameter, an dem der Kommentar der Zeile gespeichert ist, falls abweichend von `parameter`. */
    commentParameter?: string;
    parameterDef: RevaSpecOutputParameter;
    layer: string;
    commentForLayer: any;
    changedFrom: any;
    changedTo: any;
};

const TABLE_ENTRY_ADDED = "spf_added";
const TABLE_ENTRY_REMOVED = "spf_removed";

/** Formatiert „Vorschlagswert“/„Geändert zu“, inklusive neu hinzugekommener/entfernter Tabelleneinträge. */
export function formatChangelogValue(lang: RevaLanguage, parameterDef: FormatParameterDef, value: unknown) {
    if (value === TABLE_ENTRY_ADDED) return lang === "en" ? "(new)" : "(neu)";
    if (value === TABLE_ENTRY_REMOVED) return lang === "en" ? "(removed)" : "(entfernt)";
    return formatParameterValue(lang, parameterDef, value);
}

type TableEntry = Record<string, unknown>;

interface TableField {
    key: string;
    parameter: string;
    suffixDe: string;
    suffixEn: string;
    type: string;
    unit?: string;
    categories?: FormatParameterCategory[];
    /** Nur bei geänderten Einträgen eine eigene Zeile, nicht bei hinzugefügten/entfernten. */
    onlyIfChanged?: boolean;
}

/** Eine Tabelle aus zeilengleichen Arrays, die je Eintrag (über den Namen) und Feld verglichen wird. */
interface ChangelogTable {
    rowPrefix: string;
    labelDe: string;
    labelEn: string;
    summaryDe: string;
    summaryEn: string;
    defaultParameterType: string;
    /** Der erste trägt den Kommentar, ältere Kommentare liegen auch an den übrigen. */
    commentParameters: string[];
    parameters: ReadonlySet<string>;
    fields: TableField[];
    /** Einträge einer Schicht je Name, null ohne Tabelle in dieser Schicht. */
    entries: (layerValues: Record<string, unknown>) => Map<string, TableEntry> | null;
    /** Bei der Erstbewertung haben deren Ausgabeparameter Vorrang, aber nur als ganze Tabelle. */
    usesInitialValuationOutput?: boolean;
}

// #18566: firstFinalize zählt für die Tabellen nicht als eigene Schicht
const TABLE_LAYERS: string[] = ALL_SOURCES.filter((layer) => layer !== "firstFinalize");

// Doppelte Namen bekommen ein „(2)“ usw., damit sie im Vergleich nicht zusammenfallen.
function entriesByName(rows: Array<{name: string; entry: TableEntry}>): Map<string, TableEntry> {
    const entries = new Map<string, TableEntry>();
    for (const {name: baseName, entry} of rows) {
        let name = baseName;
        for (let n = 2; entries.has(name); n++) name = `${baseName} (${n})`;
        entries.set(name, entry);
    }
    return entries;
}

const SPF_TABLE: ChangelogTable = {
    rowPrefix: "special_property_features",
    labelDe: "Bes. objekt. Merkmal",
    labelEn: "Special property feature",
    summaryDe: "Bes. objekt. Merkmale",
    summaryEn: "Special property features",
    defaultParameterType: "INSPECTION_DERIVED",
    commentParameters: [SPF_PARAMS.values, SPF_PARAMS.valuesLending, SPF_PARAMS.shares, SPF_PARAMS.names],
    parameters: new Set(Object.values(SPF_PARAMS)),
    // Einheiten fest statt aus der Spec: dort steht für special_property_features_shares fälschlich „€“
    fields: [
        {key: "value", parameter: SPF_PARAMS.values, suffixDe: "", suffixEn: "", type: "double", unit: "€"},
        {
            key: "lending",
            parameter: SPF_PARAMS.valuesLending,
            suffixDe: " (Beleihungswert)",
            suffixEn: " (lending value)",
            type: "double",
            unit: "€",
        },
        {
            key: "share",
            parameter: SPF_PARAMS.shares,
            suffixDe: " (Anteil)",
            suffixEn: " (share)",
            type: "double",
            unit: "%",
        },
    ],
    entries: (layerValues) => {
        if (!hasSpfTable(layerValues)) return null;
        const spf = normalizeSpf(layerValues);
        const names = spf?.[SPF_PARAMS.names] ?? [];
        return entriesByName(
            names.map((name, i) => ({
                name: (name as string | null) || `#${i + 1}`,
                entry: Object.fromEntries(
                    SPF_TABLE.fields.map((field) => [field.key, spf?.[field.parameter]?.[i] ?? null]),
                ),
            })),
        );
    },
    usesInitialValuationOutput: true,
};

const parenthesized = (...parts: Array<string | false>) => {
    const present = parts.filter(Boolean);
    return present.length ? ` (${present.join(", ")})` : "";
};

const categories = (titles: Array<[string, string]>): FormatParameterCategory[] =>
    titles.map(([shortNameDe, shortNameEn], i) => ({categoryNumber: i + 1, shortNameDe, shortNameEn}));
const SECTION_II_CATEGORIES = categories([
    ["wertmindernd", "value-reducing"],
    ["Vorlast", "prior charge"],
    ["nein", "no"],
]);
const SECTION_II_BASES = categories([
    ["lastenfreier Wert", "unencumbered value"],
    ["Bodenwert", "ground value"],
]);

const SECTION_II_TABLE: ChangelogTable = {
    rowPrefix: "section_ii",
    labelDe: "Abt. II",
    labelEn: "Section II",
    summaryDe: "Abt. II",
    summaryEn: "Section II",
    defaultParameterType: "REGISTER",
    commentParameters: [
        SECTION_II_VALUE_FIELDS.market.value,
        ...SECTION_II_ENTRY_PARAMS.filter((p) => p !== SECTION_II_VALUE_FIELDS.market.value),
    ],
    parameters: new Set(SECTION_II_ENTRY_PARAMS),
    fields: [
        ...(["market", "lending"] as const).flatMap((line) => {
            const fields = SECTION_II_VALUE_FIELDS[line];
            const lending = line === "lending";
            const suffix = (de: string, en: string) => ({
                suffixDe: parenthesized(lending && "Beleihungswert", de),
                suffixEn: parenthesized(lending && "lending value", en),
            });
            return [
                {key: fields.value, parameter: fields.value, ...suffix("", ""), type: "double", unit: "€"},
                {
                    key: fields.percentage,
                    parameter: fields.percentage,
                    ...suffix("Anteil", "share"),
                    type: "double",
                    unit: "%",
                },
                {
                    key: fields.base,
                    parameter: fields.base,
                    ...suffix("Basis", "base"),
                    type: "category",
                    categories: SECTION_II_BASES,
                    onlyIfChanged: true,
                },
                {
                    key: fields.category,
                    parameter: fields.category,
                    ...suffix("Berücksichtigung", "consideration"),
                    type: "category",
                    categories: SECTION_II_CATEGORIES,
                    onlyIfChanged: true,
                },
            ];
        }),
        {
            key: "section_ii_entry_type_de",
            parameter: "section_ii_entry_type_de",
            suffixDe: " (Art)",
            suffixEn: " (type)",
            type: "string",
            onlyIfChanged: true,
        },
    ],
    entries: (layerValues) => {
        if (!SECTION_II_ENTRY_PARAMS.some((parameter) => Array.isArray(layerValues[parameter]))) return null;
        const defaults: TableEntry = Object.fromEntries(
            Object.values(SECTION_II_VALUE_FIELDS).map(({category, defaultCategory}) => [category, defaultCategory]),
        );
        return entriesByName(
            sectionIIRowsFromEntryParams(layerValues).map((row, i) => ({
                name: String(row.section_ii_entry_subtype_de ?? row.section_ii_entry_type_de ?? `#${i + 1}`),
                entry: Object.fromEntries(
                    SECTION_II_TABLE.fields.map((field) => [
                        field.key,
                        row[field.parameter] ?? defaults[field.key] ?? null,
                    ]),
                ),
            })),
        );
    },
};

const TABLES = [SPF_TABLE, SECTION_II_TABLE];

function findTableLayer(
    table: ChangelogTable,
    allValues: Record<string, any>,
    layers: string[],
    initialValuationOutputParams: Record<string, any> | null = null,
): {layer: string | null; entries: Map<string, TableEntry>} {
    for (const layer of layers) {
        const stored = Object.fromEntries([...table.parameters].map((p) => [p, allValues[p]?.[layer] ?? null]));
        const output =
            table.usesInitialValuationOutput && layer === "initialValuation" && initialValuationOutputParams
                ? table.entries(initialValuationOutputParams)
                : null;
        const entries = output ?? table.entries(stored);
        if (entries) return {layer, entries};
    }
    return {layer: null, entries: new Map()};
}

function tableParamDef(
    table: ChangelogTable,
    specApiParamInputs: Record<string, Record<string, RevaSpecOutputParameter>>,
    segment: string,
    field: TableField,
): RevaSpecOutputParameter {
    const existing = specApiParamInputs?.[segment]?.[field.parameter];
    return {
        parameter: field.parameter,
        parameterType: (existing?.parameterType ?? table.defaultParameterType) as any,
        type: field.type as any,
        nameDe: table.labelDe,
        nameEn: table.labelEn,
        descriptionDe: existing?.descriptionDe ?? "",
        descriptionEn: existing?.descriptionEn ?? "",
        unitDe: field.unit ?? null,
        unitEn: field.unit ?? null,
        categories: (field.categories ?? existing?.categories ?? null) as any,
    };
}

// Vergleicht alte/neue Einträge per Name und Feld; nur die erste Zeile trägt den Kommentar.
function diffTableEntries(
    table: ChangelogTable,
    oldEntries: Map<string, TableEntry>,
    newEntries: Map<string, TableEntry>,
    layer: string,
    comment: any,
    paramDefs: RevaSpecOutputParameter[],
): CommentForAttachment[] {
    const rows: CommentForAttachment[] = [];
    let commentUsed = false;
    for (const name of new Set([...oldEntries.keys(), ...newEntries.keys()])) {
        const oldEntry = oldEntries.get(name);
        const newEntry = newEntries.get(name);
        const presentEntry = (newEntry ?? oldEntry) as TableEntry;
        const changedFields = table.fields.filter((field) =>
            oldEntry && newEntry
                ? oldEntry[field.key] !== newEntry[field.key]
                : !field.onlyIfChanged && presentEntry[field.key] !== null,
        );
        // hinzugefügte/entfernte Einträge ohne jeden Wert erscheinen trotzdem einmal
        if (changedFields.length === 0 && !(oldEntry && newEntry)) changedFields.push(table.fields[0]);

        for (const field of changedFields) {
            const rowComment = commentUsed ? null : comment;
            if (rowComment) commentUsed = true;
            const paramDef = paramDefs[table.fields.indexOf(field)];
            rows.push({
                commentParameter: table.commentParameters[0],
                parameter: `${table.rowPrefix}_entry_${name}${field === table.fields[0] ? "" : `_${field.key}`}`,
                parameterDef: {
                    ...paramDef,
                    nameDe: `${table.labelDe}: ${name}${field.suffixDe}`,
                    nameEn: `${table.labelEn}: ${name}${field.suffixEn}`,
                },
                layer,
                commentForLayer: rowComment,
                changedFrom: oldEntry ? oldEntry[field.key] : TABLE_ENTRY_ADDED,
                changedTo: newEntry ? newEntry[field.key] : TABLE_ENTRY_REMOVED,
            });
        }
    }
    return rows;
}

function tableSummaryRow(
    table: ChangelogTable,
    layer: string,
    comment: any,
    paramDef: RevaSpecOutputParameter,
): CommentForAttachment[] {
    if (!comment) return [];
    return [
        {
            parameter: `${table.rowPrefix}_comment`,
            commentParameter: table.commentParameters[0],
            parameterDef: {...paramDef, nameDe: table.summaryDe, nameEn: table.summaryEn},
            layer,
            commentForLayer: comment,
            changedFrom: null,
            changedTo: null,
        },
    ];
}

function getTableCommentRows(
    table: ChangelogTable,
    allValues: Record<string, any>,
    allComments: Record<string, any>,
    specApiParamInputs: Record<string, Record<string, RevaSpecOutputParameter>>,
    segment: string,
    initialValuationOutputParams: Record<string, any> | null,
): CommentForAttachment[] {
    const {layer: activeLayer, entries: activeEntries} = findTableLayer(table, allValues, TABLE_LAYERS);
    if (!activeLayer) return [];

    const comment =
        table.commentParameters.map((parameter) => allComments[parameter]?.[activeLayer]).find((c) => c) ?? null;
    const baseLayers = TABLE_LAYERS.slice(TABLE_LAYERS.indexOf(activeLayer) + 1);
    const {layer: baseLayer, entries: baseEntries} = findTableLayer(
        table,
        allValues,
        baseLayers,
        initialValuationOutputParams,
    );
    if (!baseLayer && !comment) return [];

    const paramDefs = table.fields.map((field) => tableParamDef(table, specApiParamInputs, segment, field));
    const rows = diffTableEntries(table, baseEntries, activeEntries, activeLayer, comment, paramDefs);
    return rows.length ? rows : tableSummaryRow(table, activeLayer, comment, paramDefs[0]);
}

/**
 * Zeilen für „Änderungen/Kommentare“ (Tab Prüfung) und die Kommentar-Anlage des PDFs: je Parameter und
 * Schicht eine Zeile, wenn der Wert von der nächsten befüllten tieferen Schicht abweicht oder kommentiert
 * ist. Die Merkmale (SPF) und Abt. II werden je Eintrag und Feld verglichen statt als ganze Arrays.
 *
 * @param allValues Parameter → Schicht → Wert
 * @param allComments Parameter → Schicht → Kommentar
 */
export function buildCommentsForAttachments(
    specApiParamInputs: Record<string, Record<string, RevaSpecOutputParameter>> | null | undefined,
    segment: string,
    allValues: Record<string, any>,
    allComments: Record<string, any>,
    initialValuationOutputParams: Record<string, any> | null,
): CommentForAttachment[] {
    const comments: CommentForAttachment[] = [];
    if (!specApiParamInputs) return comments;

    for (const [parameter, allValuesForParam] of Object.entries(allValues)) {
        if (TABLES.some((table) => table.parameters.has(parameter))) continue;

        const allCommentValues = allComments[parameter];
        if (!allValuesForParam) continue;
        const rows = [];

        for (let currLayerIndex = 0; currLayerIndex < ALL_SOURCES.length; currLayerIndex++) {
            const layer = ALL_SOURCES[currLayerIndex];
            const changedTo = allValuesForParam[layer] ?? null;
            const changedToExists = changedTo !== null && changedTo !== "-";
            const parameterDef = specApiParamInputs?.[segment]?.[parameter];
            if (!parameterDef) continue;
            if (!changedToExists) continue;

            const findChangedFrom = (subLayer: string) => {
                const isInitialValuationLayer = subLayer === "initialValuation";
                return isInitialValuationLayer
                    ? (initialValuationOutputParams?.[parameter] ?? allValuesForParam[subLayer] ?? null)
                    : (allValuesForParam[subLayer] ?? null);
            };

            const commentForLayer = allCommentValues?.[layer];
            // #19169: kommentierte Werte immer eintragen; der Vergleichswert ist dann nur Information
            // (Spalte "Vorschlagswert")
            if (commentForLayer) {
                let changedFrom = null;
                for (let sublayerIndex = currLayerIndex + 1; sublayerIndex < ALL_SOURCES.length; sublayerIndex++) {
                    changedFrom = findChangedFrom(ALL_SOURCES[sublayerIndex]);
                    if (changedFrom !== null) break;
                }
                rows.push({
                    parameter,
                    parameterDef,
                    layer,
                    commentForLayer,
                    changedFrom,
                    changedTo,
                });
                continue;
            }

            for (let sublayerIndex = currLayerIndex + 1; sublayerIndex < ALL_SOURCES.length; sublayerIndex++) {
                const subLayer = ALL_SOURCES[sublayerIndex];
                const changedFrom = findChangedFrom(subLayer);
                const changedFromExists = changedFrom !== null;
                if (!changedFromExists) continue;

                if (Array.isArray(changedFrom) && Array.isArray(changedTo)) {
                    if (
                        changedFrom.length !== changedTo.length ||
                        JSON.stringify(changedFrom) !== JSON.stringify(changedTo)
                    ) {
                        rows.push({
                            parameter,
                            parameterDef,
                            layer,
                            commentForLayer,
                            changedFrom,
                            changedTo,
                        });
                        currLayerIndex = sublayerIndex - 1;
                        break;
                    }
                    continue;
                }

                if (changedTo !== changedFrom) {
                    rows.push({
                        parameter,
                        parameterDef,
                        layer,
                        commentForLayer,
                        changedFrom,
                        changedTo,
                    });
                    currLayerIndex = sublayerIndex - 1;
                    break;
                }
            }
        }
        if (rows.length) comments.push(...rows);
    }

    for (const table of TABLES) {
        comments.push(
            ...getTableCommentRows(
                table,
                allValues,
                allComments,
                specApiParamInputs,
                segment,
                initialValuationOutputParams,
            ),
        );
    }

    return comments;
}
